import {AfterViewInit, Component, ErrorHandler, Input, OnInit, ViewChild} from '@angular/core';
import { MatPaginator, MatSort, MatTableDataSource, MatDialogConfig, MatDialog, MatSnackBar, MatCheckboxChange } from '@angular/material';
import { BusinessService } from '../../business/models/business-service.model';
import { EditFeeComponent } from '../../business/edit-fee/edit-fee.component';
import { DeleteFeeComponent } from '../../business/delete-fee/delete-fee.component';
import { EditToggleService } from '../services/edit-toggle.service';
import { Business } from '../../business/models/business.model';
import { IConditionalSection } from '../models/condition-section.model';
import { FormControl } from '@angular/forms';
import { AirportService } from '../../airport/airport.service';
import { AuthService } from '../../auth/auth/auth.service';
import { IAuthEvent } from '../../auth/models/auth-event.model';
import { UserService } from '../user/user.service';
import { UserInfo } from '../models/user-info.model';
import { State } from '../state-view/state.model';


@Component({
  selector: 'app-business-fees',
  templateUrl: './business-fees.component.html',
  styleUrls: ['./business-fees.component.scss']
})
export class BusinessFeesComponent implements OnInit, AfterViewInit, IConditionalSection {

  @Input() editModeSet: boolean;
  @Input() business: Business;
  @Input() fees: Array<BusinessService>;
  @Input() feesLastUpdate: Date;

  query = '';
  formatFeeBasis = BusinessService.formatFeeBasis;
  displayedColumns = ['description', 'fee', 'feeBasis', 'aircraftType', 'serviceNote'];
  dataSource: MatTableDataSource<BusinessService>;
  aircraftFilter = new FormControl();
  aircraftTypeCheckbox = new FormControl();
  filteredValues = {
    description: '', fee: '',
    feeBasis: '', serviceNote: '', aircraftType: ''
  };
  private _userInfo: UserInfo;
  authenticated = false;
  state = State.Empty;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(private _toggleService: EditToggleService,
    private _editFeeDialog: MatDialog,
    private _deleteFeeDialog: MatDialog,
    public snackBar: MatSnackBar,
    private _auth: AuthService,
    private _userService: UserService,
    private _airportService: AirportService,
    private _errorHandler: ErrorHandler) {
  }

  ngOnInit() {
    // Update state based on latest event in authStream, and subscribe to any future
    // changes in authentication state.
    this._onAuthEvent(this._auth.authStream.getValue());
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));

    // Update state based on latest available userInfo, and subscribe to any future
    // changes to userInfo.
    this._userInfo = this._userService.userInfo.getValue();
    if (this._userInfo && this._userInfo.aircraftType) {
      this.aircraftFilter.setValue(this._userInfo.aircraftType);
    }
    this._userService.userInfo.subscribe(u => {
      // Update userInfo
      this._userInfo = u;
      // Set state to loaded
      this.state = State.Content;
      if (this._userInfo && this._userInfo.aircraftType) {
        this.aircraftFilter.setValue(this._userInfo.aircraftType);
      }
    });

    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
      if (this.editModeSet) {
        this.aircraftFilter.setValue('All Aircraft');
      }
    });

    this.setDataSource();
    /* set filter here for saved aircraft type preference */
    this.dataSource.filter = JSON.stringify(this.filteredValues);

    this.aircraftFilter.valueChanges.subscribe((aircraftFilterValue) => {
      this.filteredValues['aircraftType'] = aircraftFilterValue;
      this.dataSource.filter = JSON.stringify(this.filteredValues);
      if (this.aircraftTypeCheckbox.value) {
        // save change if checkbox checked
        this.saveAircraftType(this.aircraftFilter.value);
      }
    });
  }

  async saveAircraftType(aircraftType: string) {
    try {
      await this._userService.setAircraftType(aircraftType);
      this.snackBar.open(`${aircraftType} was set as your default aircraft type.`, 'Dismiss', {
        duration: 2000
      });
    } catch (e) {
      this.snackBar.open('An error occurred updating your default aircraft type. Please try again later.', 'Dismiss', {
        duration: 6000
      });
      this._errorHandler.handleError(e);

      // Set state to error, then reset after a few seconds to allow retry.
      this.state = State.Error;
      setTimeout(() => {
        this.state = State.Content;
      }, 2000);
    }
  }

  private _onAuthEvent(e: IAuthEvent) {
    this.authenticated = this._auth.authenticated;
    if (this.authenticated && !this._auth.endUser) {
      // userInfo is not available when not authenticated as an end user, so set favorite state to loaded now.
      this.state = State.Content;
    }
  }

  private formatFee(fee: number): string {
    return Number.isInteger(fee) ? '1.0-0' : '1.2-2';
  }

  setDataSource() {
    const fees: BusinessService[] = this.sortedFees;
    this.dataSource = new MatTableDataSource(fees);
  }

  setDataSourceFeatures() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = this.customFilterPredicate();
    /* this.dataSource.filterPredicate = (data: BusinessService, filter: string) =>
      (data.description && data.description.toLowerCase().trim().indexOf(filter) !== -1)
      || (data.feeBasis && BusinessService.formatFeeBasis(data.feeBasis).toLowerCase().trim().indexOf(filter) !== -1)
      || (data.serviceNote && data.serviceNote.toLowerCase().trim().indexOf(filter) !== -1)
      || (data.fee && (String('$' + data.fee.toFixed(2)).toLowerCase().trim().indexOf(filter) !== -1)); */
  }

  /**
   * Set the paginator, sort, and filterPredicate after the view init since this component will
   * be able to query its view for the initialized paginator and sort.
   */
  ngAfterViewInit() {
    this.setDataSourceFeatures();
    if (this._userInfo && this._userInfo.aircraftType) {
      this.aircraftFilter.setValue(this._userInfo.aircraftType);
    }
  }

  applyFilter(filter) {
    this.query = (filter || '').trim().toLowerCase();
    this.dataSource.filter = JSON.stringify(this.filteredValues);
  }

  customFilterPredicate() {
    const myFilterPredicate = (data: BusinessService, filter: string): boolean => {
      let globalMatch = !this.query;

      if (this.query) {
        // search all text fields
        globalMatch = // data.name.toString().trim().toLowerCase().indexOf(this.globalFilter.toLowerCase()) !== -1;
        (data.description && data.description.toLowerCase().trim().indexOf(this.query) !== -1)
        || (data.feeBasis && BusinessService.formatFeeBasis(data.feeBasis).toLowerCase().trim().indexOf(this.query) !== -1)
        || (data.serviceNote && data.serviceNote.toLowerCase().trim().indexOf(this.query) !== -1)
        || (data.fee && (String('$' + data.fee.toFixed(2)).toLowerCase().trim().indexOf(this.query) !== -1));
      }

      if (!globalMatch) {
        return;
      }

      const searchString = JSON.parse(filter);
      if (searchString.aircraftType) {
        // if an aircraft type has been chosen in the dropdown
        if (searchString.aircraftType === 'All Aircraft') {
          // if the dropdown is 'All Aircraft' return everything including nulls
          return true;
        } else {
          // when a specific aircraft type is selected, return nulls, 'All Aircraft', 'Unknown', and the selected type
          return (!data.aircraftType
            || data.aircraftType.toString().trim().toLowerCase().indexOf('all aircraft') !== -1
            || data.aircraftType.toString().trim().toLowerCase().indexOf('unknown') !== -1
            || data.aircraftType.toString().trim().toLowerCase().indexOf(searchString.aircraftType.toLowerCase()) !== -1);
        }
      } else {
        // no aircraft type chosen, filter everything until a type is chosen
        return;
      }
    };
    return myFilterPredicate;
  }

  get sortedFees(): Array<BusinessService> {
    return this.fees
      .sort((a, b) => {
        if (a.description < b.description) {
          return -1;
        }
        if (a.description > b.description) {
          return 1;
        }
        return 0;
      });
  }

  checkBoxDisabled(): boolean {
    if (!this._auth.authenticated || !this._auth.endUser) {
      return true;
    }
    if (this.aircraftFilter && this.aircraftFilter.value) {
      return false;
    }
    return true;
  }

  filterDisabled(): boolean {
    if (this.editModeSet) {
      return true;
    }
    return false;
  }

  async checkBoxChanged(event: MatCheckboxChange) {
    try {
      // tslint:disable-next-line:prefer-const
      let result: any;
      if (event.checked && this.aircraftFilter && this.aircraftFilter.value) {
        this.saveAircraftType(this.aircraftFilter.value);
      }
    } catch (e) {
      this.snackBar.open('Error saving aircraft type preference, please try again later', 'Error', {
        duration: 6000
      });
    }
  }

  async reloadBusiness(businessId: string) {
    this.business = await this._airportService.getBusiness(businessId);
    this.fees = this.business.services;
    this.dataSource = new MatTableDataSource(this.sortedFees);
    this.setDataSourceFeatures();
    this.aircraftFilter.setValue('All Aircraft');
  }

  openEditFeeDialog(row: BusinessService) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = {
      busID: this.business.id,
      id: null,
      isNew: false
    };
    row == null ? dialogConfig.data.isNew = true : dialogConfig.data.fee = row;
    dialogConfig.data['busID'] = this.business.id;
    dialogConfig.width = '60%';
    const dialogRef = this._editFeeDialog.open(EditFeeComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this.reloadBusiness(this.business.id.toString());
        if (isUpdated.isNew) {
          this.snackBar.open('Fee', 'Added', {
            duration: 2000
          });
        } else {
          this.snackBar.open('Fee', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this.snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteFee(row: BusinessService) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      businessID: this.business.id,
      serviceId: row.serviceId
    };
    const dialogRef = this._deleteFeeDialog.open(DeleteFeeComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this.reloadBusiness(this.business.id.toString());
        this.snackBar.open('Fee', 'Deleted', {
          duration: 2000
        });
      } else {
        this.snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  get hasContent(): boolean {
    return typeof this.fees !== 'undefined' && this.fees !== null && this.fees.length > 0;
  }

}
